import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from './config';

const containerStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  padding: '20px',
  boxSizing: 'border-box'
};

const contentStyle = {
  maxWidth: '800px',
  margin: '0 auto',
  width: '100%',
  background: 'var(--bg-tertiary)',
  padding: '30px',
  borderRadius: '8px',
  boxShadow: '0 4px 6px var(--shadow-color)',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflowY: 'auto'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: '20px'
};

const thStyle = {
  textAlign: 'left',
  padding: '12px',
  borderBottom: '1px solid var(--border-color)',
  color: 'var(--text-secondary)'
};

const tdStyle = {
  padding: '12px',
  borderBottom: '1px solid var(--border-color)'
};

const inputStyle = {
  width: '100%',
  padding: '10px',
  marginBottom: '15px',
  border: '1px solid var(--border-color)',
  borderRadius: '4px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  background: 'var(--bg-input)',
  color: 'var(--text-primary)'
};

const labelStyle = {
  display: 'block',
  marginBottom: '5px',
  fontWeight: 'bold',
  color: 'var(--text-secondary)'
};

const buttonStyle = {
  padding: '8px 16px',
  backgroundColor: 'var(--accent-color)',
  color: 'var(--text-inverse)',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  marginRight: '10px',
  fontSize: '14px'
};

const deleteButtonStyle = {
  ...buttonStyle,
  backgroundColor: 'var(--error-color)'
};

const secondaryButtonStyle = {
  ...buttonStyle,
  backgroundColor: 'var(--button-bg)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border-color)'
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000
};

const modalContentStyle = {
  background: 'var(--bg-tertiary)',
  padding: '30px',
  borderRadius: '8px',
  width: '500px',
  maxWidth: '90%',
  boxShadow: '0 4px 6px var(--shadow-color)'
};

export default function GlobalVariables({ onBack }) {
  const [variables, setVariables] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    key: '',
    value: '',
    description: '',
    is_secret: false
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchVariables();
  }, []);

  const fetchVariables = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/variables`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!res.ok) {
        throw new Error('Failed to fetch variables');
      }
      
      const data = await res.json();
      setVariables(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleOpenAdd = () => {
    setFormData({
      key: '',
      value: '',
      description: '',
      is_secret: false
    });
    setIsEditing(false);
    setIsModalOpen(true);
    setError(null);
  };

  const handleOpenEdit = (variable) => {
    setFormData({
      key: variable.key,
      value: variable.is_secret ? '' : variable.value, // Don't show masked value
      description: variable.description || '',
      is_secret: variable.is_secret
    });
    setIsEditing(true);
    setIsModalOpen(true);
    setError(null);
  };

  const handleDelete = async (key) => {
    if (!(await window.confirm(`Are you sure you want to delete variable "${key}"?`))) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/variables/${key}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error('Failed to delete variable');

      fetchVariables();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const url = isEditing 
        ? `${API_BASE_URL}/variables/${formData.key}`
        : `${API_BASE_URL}/variables`;
      
      const method = isEditing ? 'PUT' : 'POST';
      
      const body = {
        description: formData.description,
        is_secret: formData.is_secret
      };

      if (!isEditing) {
        body.key = formData.key;
        body.value = formData.value;
      } else {
        // Only send value if it's changed (not empty string for secrets unless intent is to clear)
        // For secrets, we usually leave it blank to keep existing value
        if (formData.value !== '') {
            body.value = formData.value;
        }
      }

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to save variable');
      }

      setIsModalOpen(false);
      fetchVariables();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button onClick={onBack} style={{ ...secondaryButtonStyle, marginRight: '15px' }}>
              ← Back
            </button>
            <h2 style={{ margin: 0 }}>Global Variables</h2>
          </div>
          <button onClick={handleOpenAdd} style={buttonStyle}>
            + Add Variable
          </button>
        </div>

        {error && <div style={{ color: 'var(--error-color)', marginBottom: '15px' }}>{error}</div>}

        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Key</th>
                <th style={thStyle}>Value</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {variables.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ ...tdStyle, textAlign: 'center' }}>No variables found</td>
                </tr>
              ) : (
                variables.map(v => (
                  <tr key={v.key}>
                    <td style={tdStyle}><code>{v.key}</code></td>
                    <td style={tdStyle}>
                      {v.is_secret ? (
                        <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>********</span>
                      ) : (
                        v.value.length > 50 ? v.value.substring(0, 50) + '...' : v.value
                      )}
                    </td>
                    <td style={tdStyle}>{v.description}</td>
                    <td style={tdStyle}>
                      <button 
                        onClick={() => handleOpenEdit(v)} 
                        style={{ ...secondaryButtonStyle, padding: '5px 10px', fontSize: '12px' }}
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDelete(v.key)} 
                        style={{ ...deleteButtonStyle, padding: '5px 10px', fontSize: '12px' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={{ marginTop: 0 }}>{isEditing ? 'Edit Variable' : 'Add Variable'}</h3>
            <form onSubmit={handleSubmit}>
              {!isEditing && (
                <div style={{ marginBottom: '15px' }}>
                  <label style={labelStyle}>Key</label>
                  <input
                    type="text"
                    value={formData.key}
                    onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                    style={inputStyle}
                    required
                    placeholder="e.g. API_KEY"
                  />
                </div>
              )}
              
              <div style={{ marginBottom: '15px' }}>
                <label style={labelStyle}>Value {isEditing && formData.is_secret && '(Leave blank to keep unchanged)'}</label>
                <input
                  type={formData.is_secret ? "password" : "text"}
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  style={inputStyle}
                  placeholder={isEditing && formData.is_secret ? "********" : "Value"}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={labelStyle}>Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={inputStyle}
                  placeholder="Optional description"
                />
              </div>

              <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id="is_secret"
                  checked={formData.is_secret}
                  onChange={(e) => setFormData({ ...formData, is_secret: e.target.checked })}
                  style={{ marginRight: '10px' }}
                />
                <label htmlFor="is_secret" style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }}>
                  Treat as Secret (Encrypted)
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)} 
                  style={secondaryButtonStyle}
                >
                  Cancel
                </button>
                <button type="submit" style={buttonStyle}>
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
